import { useCallback, useEffect, useMemo, useState } from 'react';
import { InputField, ReadonlyField, SectionTitle, TypeSelector, DeactivateLink, ConfirmModal } from './UserFormFields';
import type { CreateUserForm, UserToEdit, UserDetails } from '../../types/team.types';
import { formatCurrencyInput } from '@/features/card/components/ActivateCardModal/activateCard.validators';
import { isValidEmail, formatCurrency } from '@chikara/utils';
import { useAuthContext } from '@/features/auth/context/AuthContext/AuthContext';
import { customerService } from '@/features/card/services/customerService';
import { useSpinner } from '@chikara/ui';
import { UserFormModal } from './UserFormModal';
import { useUserForm } from './useUserForm';
import { MfaModal } from '@/features/auth/components/MfaFlow/MfaModal';
import { mfaService } from '@/features/auth/services/mfaService';
import { t } from '@/i18n';

export type UserModalMode = 'add' | 'edit' | 'approve';

export interface UserModalProps {
  isOpen: boolean;
  mode: UserModalMode;
  user?: UserToEdit | UserDetails | null;
  onClose: () => void;
  onSubmit: (values: any) => Promise<void> | void;
  onReject?: (member_uuid: string) => Promise<void> | void;      // Used for approve
  onDeactivate?: (member_uuid: string) => Promise<void> | void;  // Used for edit
  isSubmitting?: boolean;
}

export function UserModal({
  isOpen,
  mode,
  user,
  onClose,
  onSubmit,
  onReject,
  onDeactivate,
  isSubmitting = false,
}: UserModalProps) {
  const { values, handleChange, setValues, resetForm } = useUserForm();
  const [errors, setErrors] = useState<Partial<Record<keyof CreateUserForm, string>>>({});
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [globalAvailable, setGlobalAvailable] = useState<number>(0);
  const [showMfa, setShowMfa] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [pendingValues, setPendingValues] = useState<any>(null);
  const { payload: authPayload } = useAuthContext();
  const sameUser = authPayload?.user_uuid === user?.uuid;
  const { show, hide, isSpinnerLoading: isGlobalLoading } = useSpinner();

  const isAdd = mode === 'add';
  const isEdit = mode === 'edit';
  const isApprove = mode === 'approve';

  // Extract titles and labels based on mode
  const title = isAdd ? t('team.modals.addUser.title') : isEdit ? t('team.modals.editUser.title') : t('team.modals.approveUser.title');
  const description = isAdd ? t('team.modals.addUser.description') : isEdit ? t('team.modals.editUser.description') : t('team.modals.approveUser.description');
  const primaryLabel = isAdd ? t('team.modals.addUser.submit') : isEdit ? t('team.actions.save') : t('team.modals.approveUser.approve');
  const secondaryLabel = isApprove ? t('team.modals.approveUser.reject') : isEdit ? t('team.actions.back') : undefined;

  // Reset form when opening/closing or changing user
  useEffect(() => {
    if (isOpen) {
      if (isAdd) {
        resetForm();
      } else if (user) {
        setValues({
          uuid: user.uuid,
          role: user.role ?? t('team.user.role.basic'),
          email: user.email ?? '',
          name: user.name ?? '',
          lname: user.lname ?? '',
          slname: user.slname ?? '',
          monthly_limit: user.monthly_limit !== undefined && user.monthly_limit !== null ? formatCurrency(Number(user.monthly_limit)) : '',
          phone: user.phone ?? '',
          account_uuid: user.account_uuid,
          origin: {
            source: 'invitation'
          }
        });
      }
    } else {
      setErrors({});
      setShowDeactivateConfirm(false);
      setGlobalAvailable(0);
      setShowMfa(false);
      setMfaToken(null);
      setMfaError(null);
      setPendingValues(null);
    }
  }, [isOpen, user, isAdd, isEdit, resetForm, setValues]);

  useEffect(() => {
    const fetchSummary = async () => {
      if (isOpen && authPayload?.customer_uuid) {
        show();
        try {
          const response = await customerService.getCustomerSummary();
          setGlobalAvailable(response.credit_line.total_available_to_assign);
        } catch (error) {
          console.error('Error fetching customer summary:', error);
        } finally {
          hide();
        }
      }
    };

    fetchSummary();
  }, [authPayload?.customer_uuid, show, hide, isOpen]);

  const isLimitExceeded = useMemo(() => {
    const numericLimit = values.monthly_limit ? Number(values.monthly_limit.replace(/[$,]/g, "")) : 0;
    return !!values.monthly_limit && numericLimit > globalAvailable;
  }, [values.monthly_limit, globalAvailable]);

  // Phone validation: detect country code and validate expected digit length
  const validatePhone = useCallback((phone: string): string | undefined => {
    if (!phone) return undefined;
    // Must start with '+'
    if (!phone.startsWith('+')) return t('team.validations.phoneInvalid');
    // Only '+' followed by digits allowed
    if (!/^\+\d+$/.test(phone)) return t('team.validations.phoneInvalid');
    const digits = phone.slice(1); // digits after '+'
    const totalDigits = digits.length;
    // General international rule: 10-15 digits total (including country code)
    if (totalDigits < 10 || totalDigits > 15) return t('team.validations.phoneInvalid');
    // Country-specific validation by country code prefix
    const countryLengthMap: Array<{ prefix: string; subscriberDigits: number }> = [
      { prefix: '52',  subscriberDigits: 10 }, // Mexico
      { prefix: '1',   subscriberDigits: 10 }, // USA / Canada
      { prefix: '34',  subscriberDigits: 9  }, // Spain
      { prefix: '57',  subscriberDigits: 10 }, // Colombia
      { prefix: '54',  subscriberDigits: 10 }, // Argentina
      { prefix: '55',  subscriberDigits: 11 }, // Brazil
      { prefix: '56',  subscriberDigits: 9  }, // Chile
      { prefix: '51',  subscriberDigits: 9  }, // Peru
      { prefix: '44',  subscriberDigits: 10 }, // UK
      { prefix: '49',  subscriberDigits: 10 }, // Germany
      { prefix: '33',  subscriberDigits: 9  }, // France
    ];
    const match = countryLengthMap.find(({ prefix }) => digits.startsWith(prefix));
    if (match) {
      const subscriberPart = digits.slice(match.prefix.length);
      if (subscriberPart.length !== match.subscriberDigits) {
        return t('team.validations.phoneInvalidLength');
      }
    }
    return undefined;
  }, []);

  // Validation logic
  const isFormComplete = useMemo(() => {
    if (isLimitExceeded && !isEdit) return false;
    if (isAdd) {
      const phoneOk = !validatePhone(values.phone);
      return (
        values.email.trim() !== '' &&
        values.monthly_limit !== '' &&
        phoneOk
      );
    }
    if (isApprove) {
      return values.monthly_limit !== '';
    }
    return true; // Edit mode
  }, [values, isAdd, isApprove, isLimitExceeded, validatePhone]);

  const validate = useCallback((): boolean => {
    const next: Partial<Record<keyof CreateUserForm, string>> = {};
    if (isAdd || isEdit) {
      if (!values.email?.trim()) {
        next.email = t('team.validations.emailRequired');
      } else if (!isValidEmail(values.email.trim())) {
        next.email = t('team.validations.emailInvalid');
      }
      const phoneError = validatePhone(values.phone ?? '');
      if (phoneError) next.phone = phoneError;
    }
    const numericLimit = values.monthly_limit ? Number(values.monthly_limit.replace(/[$,]/g, "")) : 0;
    if (values.monthly_limit && (Number.isNaN(numericLimit) || numericLimit <= 0)) {
      next.monthly_limit = t('team.validations.spendingLimitInvalid');
    } else if (values.monthly_limit && numericLimit > globalAvailable) {
      next.monthly_limit = t('dashboard.card.detail.modals.manageLimit.errors.exceeded');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [values.email, values.phone, values.monthly_limit, isAdd, isEdit, globalAvailable, validatePhone]);

  const handleSubmit = useCallback(async () => {
    if ((isEdit || isApprove) && !user) return;
    if (!validate()) return;
    try {
      const parsedLimit = values.monthly_limit ? Number(values.monthly_limit.replace(/[$,]/g, '')) : 0;
      let payload: any = {
        ...values,
        name: values.name.trim(),
        lname: values.lname.trim(),
        slname: values.slname?.trim() || '',
        monthly_limit: parsedLimit,
      };

      if (isEdit) {
        const diffPayload: any = { uuid: values.uuid };
        let hasChanges = false;

        // Compare each field
        if (values.role !== (user?.role ?? t('team.user.role.basic'))) {
          diffPayload.role = values.role;
          hasChanges = true;
        }
        if (values.email.trim() !== (user?.email ?? '')) {
          diffPayload.email = values.email.trim();
          hasChanges = true;
        }
        
        const trimmedName = values.name.trim();
        if (trimmedName !== (user?.name ?? '')) {
          diffPayload.name = trimmedName;
          hasChanges = true;
        }
        
        const trimmedLname = values.lname.trim();
        if (trimmedLname !== (user?.lname ?? '')) {
          diffPayload.lname = trimmedLname;
          hasChanges = true;
        }
        
        const trimmedSlname = values.slname?.trim() || '';
        if (trimmedSlname !== (user?.slname ?? '')) {
          diffPayload.slname = trimmedSlname;
          hasChanges = true;
        }
        
        if (values.phone !== (user?.phone ?? '')) {
          diffPayload.phone = values.phone;
          hasChanges = true;
        }
        
        const originalLimit = user?.monthly_limit !== undefined && user?.monthly_limit !== null ? Number(user.monthly_limit) : 0;
        if (parsedLimit !== originalLimit) {
          diffPayload.monthly_limit = parsedLimit;
          hasChanges = true;
        }

        if (!hasChanges) {
          onClose();
          return;
        }
        payload = diffPayload;
      }

      if (isEdit && !showMfa) {
        show();
        try {
          const { data } = await mfaService.mfaSend({
            command: 'user-update',
            type: 'user-update',
          });
          setMfaToken(data.mfa_token);
          setPendingValues(payload);
          setShowMfa(true);
        } catch {
          setMfaError(t('mfaError.send'));
        } finally {
          hide();
        }
        return;
      }

      await onSubmit(payload as any);
      if (isAdd) {
        resetForm();
        setErrors({});
      }
      onClose();
    } catch (error) {
      console.error(t('team.errors.save'), error);
    }
  }, [validate, onSubmit, values, user, isAdd, isEdit, isApprove, resetForm, onClose, showMfa, show, hide]);

  const handleVerifyMfa = async (otp: string) => {
    if (!mfaToken) return;
    setMfaError(null);
    show();
    try {
      await mfaService.mfaVerify(otp, {
        command: 'user-update',
        type: 'user-update',
        mfa_token: mfaToken,
      });

      if (pendingValues) {
        setShowMfa(false); setMfaToken(null); setMfaError(null);
        await onSubmit(pendingValues);
        onClose();
      }
    } catch (error) {
      console.error(t('team.errors.save'), error);
      setMfaError(t('team.errors.save'));
    } finally {
      hide();
    }
  };

  const handleSecondaryAction = useCallback(async () => {
    if (isApprove && user && onReject) {
      try {
        await onReject(user.uuid);
        onClose();
      } catch (error) {
        console.error(t('team.errors.reject'), error);
      }
    } else if (isEdit) {
      onClose();
    }
  }, [isApprove, isEdit, user, onReject, onClose]);

  const handlePhoneBlur = useCallback(() => {
    if (isAdd || isEdit) {
      const phoneError = validatePhone(values.phone ?? '');
      setErrors((prev) => ({ ...prev, phone: phoneError }));
    }
  }, [isAdd, isEdit, validatePhone, values.phone]);

  const handleClose = useCallback(() => {
    resetForm();
    setErrors({});
    setShowDeactivateConfirm(false);
    onClose();
  }, [onClose, resetForm]);

  if ((isEdit || isApprove) && !user) return null;

  return (
    <>
      <UserFormModal
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        description={description}
        primaryActionLabel={primaryLabel}
        onPrimaryAction={handleSubmit}
        secondaryActionLabel={secondaryLabel}
        onSecondaryAction={secondaryLabel ? handleSecondaryAction : undefined}
        isLoading={isSubmitting}
        isPrimaryDisabled={!isFormComplete}
      >
        {isApprove || isEdit ? (
          <ReadonlyField label={t('team.user.fields.email')} value={user?.email || ''} variant="muted" />
        ) : (
          <InputField type="email" value={values.email} onChange={(v) => handleChange('email', v)} placeholder={t('team.user.fields.email')} error={errors.email} />
        )}

        {isApprove ? (
          <ReadonlyField label={t('team.user.fields.firstName')} value={user?.name || '-'} variant="muted" />
        ) : (
          <InputField value={values.name} onChange={(v) => handleChange('name', v)} placeholder={t('team.user.fields.firstName')} />
        )}

        {isApprove ? (
          <ReadonlyField label={t('team.user.fields.lastName')} value={user?.lname || '-'} variant="muted" />
        ) : (
          <InputField value={values.lname} onChange={(v) => handleChange('lname', v)} placeholder={t('team.user.fields.lastName')} />
        )}

        {isApprove ? (
          <ReadonlyField label={t('team.user.fields.motherLastName')} value={user?.slname || '-'} variant="muted" />
        ) : (
          <InputField value={values.slname || ''} onChange={(v) => handleChange('slname', v)} placeholder={t('team.user.fields.motherLastName')} />
        )}

        {isApprove ? (
            <ReadonlyField label={t('team.user.fields.phone')} value={user?.phone || '-'} variant="muted" />
        ) : (
            <InputField
              type="tel"
              label={t('team.user.fields.phone')}
              value={values.phone}
              onChange={(v) => handleChange('phone', v)}
              onBlur={handlePhoneBlur}
              placeholder="+525512345678"
              maxLength={16}
              error={errors.phone}
              hint={t('team.validations.phoneHelperText')}
            />
        )}
        
        {isEdit && <SectionTitle>{t('team.modals.editUser.cardsSection')}</SectionTitle>}
        {isEdit && <ReadonlyField label={t('team.modals.editUser.assignedCards')} value={(user as UserToEdit)?.account?.account_count ?? 0} variant="muted" />}

        <InputField
          inputMode="numeric"
          disabled={isEdit}
          value={formatCurrencyInput(values.monthly_limit)}
          onChange={(v) => handleChange('monthly_limit', formatCurrencyInput(v))}
          onBlur={() => validate()}
          placeholder={t('team.user.fields.spendingLimit')}
          error={errors.monthly_limit}
          hint={globalAvailable > 0 ? t('dashboard.card.detail.modals.approveCard.creditLimitAvailable', { amount: formatCurrency(globalAvailable) }) : undefined}
          hintVariant={isLimitExceeded ? 'error' : 'default'}
        />

        <TypeSelector label={t('team.user.typeLabel')} value={values.role} onChange={(v) => handleChange('role', v)} disabled={(isAdd && !isFormComplete) || sameUser} />

        {isEdit && onDeactivate && (
          <DeactivateLink onClick={() => setShowDeactivateConfirm(true)} disabled={isSubmitting || sameUser} />
        )}
      </UserFormModal>

      <MfaModal
        isOpen={showMfa}
        onClose={() => { setShowMfa(false); setMfaToken(null); setMfaError(null); }}
        onVerify={handleVerifyMfa}
        isLoading={isSubmitting || isGlobalLoading}
        error={mfaError ?? undefined}
      />

      {isEdit && onDeactivate && (
        <ConfirmModal
          isOpen={showDeactivateConfirm}
          title={t('team.modals.deactivateConfirm.title')}
          description={t('team.modals.deactivateConfirm.description')}
          confirmLabel={t('team.actions.deactivate')}
          cancelLabel={t('team.actions.cancel')}
          onConfirm={async () => {
            if (user) await onDeactivate(user.uuid);
            setShowDeactivateConfirm(false);
          }}
          onCancel={() => setShowDeactivateConfirm(false)}
          isLoading={isSubmitting}
        />
      )}
    </>
  );
}
